/**
 * The automation half of the KnowIt adapter: flows, runs, schedules, agents, dispatches and
 * the activity stream.
 *
 * It lives beside `knowit.ts` rather than inside it because it translates a *different* wire
 * convention. The document surface is Outline-style RPC with a `{ data, policies }` envelope;
 * these endpoints predate that and answer with bespoke objects — `{ flows, activeCount }`,
 * `{ runs }`, `{ steps }`, `{ lines }`, `{ tasks }`, `{ dispatches }`. Only `events.list`
 * carries the envelope. Keeping the two conventions in separate files is what stops the
 * envelope helpers from being quietly wrong for half the app.
 *
 * The permission story differs too, and deliberately:
 *
 * - **Runs** carry their own policy. The server computes `can: { pause, resume, cancel, retry,
 *   approve, … }` per run — not from a role but from whether that verb has an executor to
 *   reach — so it maps straight onto `Run.abilities` and the UI reads it like any other.
 * - **Flows, schedules and agents** have no per-entity policy on the wire. The server still
 *   enforces (flow view/run grants, decision ownership); what it does not do is *project* the
 *   answer. So control is gated on the workspace policy the session already carries, and a
 *   refusal surfaces as the server's own message. Inventing a per-flow ability here would be
 *   the client deriving permissions, which is exactly what the app must never do.
 */
import type {
  ActivityQuery,
  FlowRunRequest,
  LedgerQuery,
  ReportQuery,
  RunApproval,
  RunControl,
  RunQuery,
  ScheduledTaskDraft,
} from "~/api/client";
import { FlowValidationError } from "~/api/errors";
import type {
  ActionRow,
  ArtifactContent,
  BudgetAck,
  ChildRunSummary,
  DigestTaskDraft,
  Directive,
  FlowTaskDraft,
  IdeSession,
  JumpAck,
  JumpRequest,
  LedgerPage,
  LedgerReport,
  PublishOptions,
  PublishResult,
  RouteProposal,
  RouteSurface,
  RunArtifact,
  RunDetail,
  RunEfficiency,
  RunTree,
  StepAck,
  StopAck,
  TaskBoard,
  TaskRow,
  TriggerDraft,
} from "~/api/opsTypes";
import { normalizeRouteProposal } from "~/api/opsTypes";
import {
  ABILITY,
  type Abilities,
  type ActivityPage,
  type Agent,
  type Dispatch,
  FLOW_STATUSES,
  type Flow,
  type FlowDefinitionResult,
  type FlowDetail,
  type FlowDocAttachment,
  type FlowList,
  type FlowNodeHistory,
  type FlowRunAck,
  type FlowSaveResult,
  type FlowStatus,
  type FlowVersion,
  type Run,
  type RunLogLine,
  type RunStats,
  type RunStep,
  type ScheduledTask,
} from "~/api/types";

/** What the automation surface needs from the transport it is mounted on. */
export interface AutomationTransport {
  post(path: string, body?: Record<string, unknown>): Promise<unknown>;
  /** A few of these routes are REST-shaped GETs; POSTing to `thinkers` would *create* one. */
  get(path: string): Promise<unknown>;
  /** The session's workspace policy, already fetched and cached by the main adapter. */
  workspaceAbilities(): Promise<Abilities>;
}

export type Json = Record<string, unknown>;

// Shared with knowitAi.ts: generic JSON coercion carries no wire convention, so exporting it
// is safe where sharing the envelope helpers would not be.
export function obj(value: unknown): Json {
  return value && typeof value === "object" ? (value as Json) : {};
}

export function list(value: unknown): Json[] {
  return Array.isArray(value) ? value.map(obj) : [];
}

export function str(value: unknown, fallback = ""): string {
  return typeof value === "string" ? value : fallback;
}

export function strOrNull(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

export function num(value: unknown, fallback = 0): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

export function numOrNull(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

export function bool(value: unknown): boolean {
  return value === true;
}

/** KnowIt writes `Active`; Slate's vocabulary is lowercase. An unknown state reads as a draft. */
function flowStatus(value: unknown): FlowStatus {
  const lowered = str(value).toLowerCase();
  return (FLOW_STATUSES as readonly string[]).includes(lowered) ? (lowered as FlowStatus) : "draft";
}

/**
 * ...and back. `AiFlowStatus` is a C# enum whose wire form is its PascalCase NAME; the server
 * parses case-insensitively today, so this is belt-and-braces rather than a live bug — but it
 * is the difference between sending what the contract says and sending what happens to work
 * (W3 §F-4). A golden fixture pins both directions.
 */
function flowStatusWire(status: FlowStatus): string {
  return status.charAt(0).toUpperCase() + status.slice(1);
}

function strings(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === "string")
    : [];
}

/**
 * The definition arrives as a STRING of JSON. A definition that will not parse yields `null`
 * rather than an exception: the record around it is still readable, and a studio that refuses
 * to open a corrupt flow is a studio that cannot repair one.
 */
function parseDefinition(value: unknown): unknown {
  const text = str(value);
  if (text.trim() === "") {
    return null;
  }
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
}

/**
 * `ai-flows.save` answers in THREE shapes (W3 §C): the bare `AiFlowSaveResult`, and two
 * envelopes — `{ok, saved, permissionsSynced: true, result}` and its `false` twin with
 * `permissionsErrors[]` — which appear the moment a definition carries a `permissions` block.
 * Reading `raw.version` works right up until somebody adds one, and then quietly reports 0.
 */
function unwrapSaveResult(raw: unknown): FlowSaveResult {
  const envelope = obj(raw);
  const result = "result" in envelope ? obj(envelope.result) : envelope;
  return {
    ok: result.ok !== false,
    error: strOrNull(result.error),
    version: numOrNull(result.version),
    versionId: strOrNull(result.versionId),
    unchanged: bool(result.unchanged),
    warnings: strings(result.warnings),
    // A permission-sync failure is not a validation failure — the version WAS minted — but it is
    // the author's business, so it rides out with the warnings rather than vanishing.
    errors: [...strings(result.errors), ...strings(envelope.permissionsErrors)],
  };
}

/**
 * A refused save is an HTTP 400, and its `errors[]` are the only account the author gets of the
 * rules the client cannot check. Turning it back into an ordinary result — rather than letting
 * it throw — is what lets the studio render server refusals in the same band as its own
 * diagnostics instead of a toast that disappears.
 */
function saveRefusal(error: unknown): FlowSaveResult | null {
  if (!(error instanceof FlowValidationError)) {
    return null;
  }
  return {
    ok: false,
    error: "invalid_flow",
    version: null,
    versionId: null,
    unchanged: false,
    warnings: error.warnings,
    errors: error.errors.length > 0 ? error.errors : [error.message],
  };
}

/**
 * A step's output is the whole model answer — one run measured 108 KB across nine steps. The
 * list is rendered as a preview, so it is cut here rather than held in the query cache in full.
 */
const OUTPUT_PREVIEW_CHARS = 2000;

function preview(value: unknown, serverTruncated: boolean): { text: string; truncated: boolean } {
  const text = str(value);
  if (text.length <= OUTPUT_PREVIEW_CHARS) {
    return { text, truncated: serverTruncated };
  }
  return { text: `${text.slice(0, OUTPUT_PREVIEW_CHARS)}…`, truncated: true };
}

export function createKnowItAutomation(transport: AutomationTransport) {
  /** Flow and schedule control both hang off "may administer this workspace". */
  const manageAbilities = async (keys: string[]): Promise<Abilities> => {
    const workspace = await transport.workspaceAbilities();
    if (workspace[ABILITY.workspaceUpdate] !== true) {
      return {};
    }
    return Object.fromEntries(keys.map((key) => [key, true]));
  };

  /** One read of the flow record. `version` asks for a historical one instead of the head. */
  const flowRecord = async (slug: string, version?: number): Promise<Json> =>
    obj(
      await transport.post("ai-flows.get", {
        slug,
        ...(version === undefined ? {} : { version }),
      }),
    );

  const toFlowDetail = (raw: Json, slug: string): FlowDetail => ({
    slug: str(raw.slug, slug),
    name: str(raw.name) || slug,
    description: str(raw.description),
    status: flowStatus(raw.status),
    kind: str(raw.kind),
    version: num(raw.version),
    versionCount: num(raw.versionCount),
    nodes: list(raw.nodes).map((node) => ({
      id: str(node.nodeId),
      name: str(node.name) || str(node.nodeId),
      model: strOrNull(node.model),
      effort: strOrNull(node.effort),
      purpose: strOrNull(node.purpose),
      requireApproval: bool(node.requireApproval),
      tokens: num(node.bodyTokens),
    })),
    // The definition is kept as the server's own string, unparsed and unedited. Re-serializing
    // it here would be the first place a field could silently change shape.
    definitionJson: str(raw.definitionJson),
    documentId: strOrNull(raw.docDocumentId),
  });

  const toFlow = (raw: Json, abilities: Abilities): Flow => ({
    id: str(raw.id),
    slug: str(raw.slug),
    name: str(raw.name) || str(raw.slug),
    status: flowStatus(raw.status),
    kind: str(raw.kind),
    versionCount: num(raw.versionCount),
    nodeCount: num(raw.nodeCount),
    documentId: strOrNull(raw.docDocumentId),
    updatedAt: str(raw.updatedAt, new Date(0).toISOString()),
    abilities,
    entityLinkKind: str(raw.entityLinkKind, "workspace"),
    entityId: strOrNull(raw.entityId),
    // Not `list()`: that helper maps every entry through `obj()`, which turns a string into `{}`.
    // Categories arrive as a string array, so they are filtered rather than coerced.
    categories: Array.isArray(raw.categories)
      ? raw.categories.filter((entry): entry is string => typeof entry === "string")
      : [],
  });

  const toRun = (raw: Json): Run => {
    const can = obj(raw.can);
    const steps = obj(raw.steps);
    // `can` IS the policy: the server already decided which verbs have an executor to reach.
    // A live wire quirk (F-4): a server-hosted flow run reports can.step/jump/cancel = false
    // even though the endpoints would accept it — honoured here anyway, never guessed around.
    const abilities: Abilities = {};
    for (const key of [
      ABILITY.runPause,
      ABILITY.runResume,
      ABILITY.runCancel,
      ABILITY.runRetry,
      ABILITY.runApprove,
      ABILITY.runStep,
      ABILITY.runJump,
    ]) {
      if (can[key] === true) {
        abilities[key] = true;
      }
    }

    return {
      id: str(raw.runId),
      aiFlowSlug: strOrNull(raw.aiFlowSlug),
      name: str(raw.name) || str(raw.kind),
      kind: str(raw.kind),
      status: str(raw.status, "requested"),
      createdAt: str(raw.createdAt, new Date(0).toISOString()),
      startedAt: strOrNull(raw.startedAt),
      finishedAt: strOrNull(raw.finishedAt),
      durationMs: numOrNull(raw.durationMs),
      currentNodeId: strOrNull(raw.currentNodeId),
      result: strOrNull(raw.result),
      error: strOrNull(raw.error),
      steps: {
        total: num(steps.total),
        completed: num(steps.completed),
        failed: num(steps.failed),
      },
      awaitingApprovalNodeId: strOrNull(raw.awaitingApprovalNodeId),
      degradedSources: num(raw.degradedSources),
      degraded: list(raw.degraded).map((entry) => ({
        nodeId: str(entry.nodeId),
        sourceId: str(entry.sourceId),
        kind: str(entry.kind),
        reason: str(entry.reason),
      })),
      causalRunId: strOrNull(raw.causalRunId),
      abilities,
    };
  };

  const toChildRun = (raw: Json): ChildRunSummary => ({
    runId: str(raw.runId),
    name: str(raw.name) || str(raw.runId),
    status: str(raw.status, "requested"),
    aiFlowSlug: strOrNull(raw.aiFlowSlug),
    startedAt: strOrNull(raw.startedAt),
    endedAt: strOrNull(raw.endedAt),
    currentNodeId: strOrNull(raw.currentNodeId),
    nodesDone: num(raw.nodesDone),
    nodesTotal: num(raw.nodesTotal),
    via: str(raw.via) === "amend" ? "amend" : "subflow",
  });

  /** The draft's trigger in the server's words (scheduled-flows.create). */
  const serverTrigger = (trigger: TriggerDraft) =>
    trigger.kind === "every"
      ? { kind: "every", everyMinutes: trigger.everyMinutes }
      : trigger.kind === "on"
        ? { kind: "on", onEvent: trigger.onEvent }
        : { kind: "at", atUtc: trigger.atUtc };

  const toTask = (raw: Json, abilities: Abilities): ScheduledTask => ({
    id: str(raw.decisionId),
    title: str(raw.title) || str(raw.taskType, "Task"),
    taskType: str(raw.taskType, "flow"),
    flowId: strOrNull(raw.flowId),
    triggerKind: str(raw.triggerKind).toLowerCase(),
    recurrence: strOrNull(raw.recurrence),
    onEvent: strOrNull(raw.onEvent),
    paused: str(raw.state).toLowerCase() === "paused",
    nextFireAt: strOrNull(raw.nextFireAt),
    lastFiredAt: strOrNull(raw.lastFiredAt),
    abilities,
  });

  const healthLevel = (value: unknown): "ok" | "warn" | "error" =>
    value === "warn" || value === "error" ? value : "ok";

  const toTaskRow = (raw: Json): TaskRow => {
    const health = obj(raw.health);
    const schedule = raw.schedule ? obj(raw.schedule) : null;
    const progress = raw.progress ? obj(raw.progress) : null;
    const spend = raw.spend ? obj(raw.spend) : null;
    const target = raw.target ? obj(raw.target) : null;
    const links = obj(raw.links);
    return {
      id: str(raw.id),
      source: raw.source === "run" || raw.source === "dispatch" ? raw.source : "scheduled",
      title: str(raw.title),
      kind: str(raw.kind),
      state: str(raw.state),
      schedule: schedule
        ? {
            nextFireAt: strOrNull(schedule.nextFireAt),
            lastFiredAt: strOrNull(schedule.lastFiredAt),
            expiresAt: strOrNull(schedule.expiresAt),
            recurrence: strOrNull(schedule.recurrence),
            triggerKind: strOrNull(schedule.triggerKind),
          }
        : null,
      progress: progress
        ? {
            nodesDone: num(progress.nodesDone),
            nodesTotal: num(progress.nodesTotal),
            cursorNode: strOrNull(progress.cursorNode),
          }
        : null,
      spend: spend
        ? {
            tokens: numOrNull(spend.tokens),
            input: numOrNull(spend.input),
            cacheWrite: numOrNull(spend.cacheWrite),
            cacheRead: numOrNull(spend.cacheRead),
            output: numOrNull(spend.output),
            budget: numOrNull(spend.budget),
            requests: numOrNull(spend.requests),
            actions: numOrNull(spend.actions),
            amplification: numOrNull(spend.amplification),
            cacheReadShare: numOrNull(spend.cacheReadShare),
          }
        : null,
      target: target
        ? {
            ideSessionKey: strOrNull(target.ideSessionKey),
            machine: strOrNull(target.machine),
            workspace: strOrNull(target.workspace),
            agent: strOrNull(target.agent),
            telemetry:
              target.telemetry === "partial"
                ? "partial"
                : target.telemetry === "full"
                  ? "full"
                  : null,
            executorPid: numOrNull(target.executorPid),
            canStep: bool(target.canStep),
            stepMode: bool(target.stepMode),
            ideLive: bool(target.ideLive),
          }
        : null,
      health: { level: healthLevel(health.level), note: strOrNull(health.note) },
      // §B.1 F-8: the shape genuinely differs by source (flowId vs flowSlug); kept honest here
      // rather than coerced into one field.
      links: {
        decisionId: strOrNull(links.decisionId),
        flowId: strOrNull(links.flowId),
        flowSlug: strOrNull(links.flowSlug),
        flowVersion: numOrNull(links.flowVersion),
        run: strOrNull(links.run),
      },
      lastActionAt: strOrNull(raw.lastActionAt),
    };
  };

  const toActionRow = (raw: Json): ActionRow => {
    const tokens = obj(raw.tokens);
    return {
      id: str(raw.id),
      runId: strOrNull(raw.runId),
      stepId: strOrNull(raw.stepId),
      seq: num(raw.seq),
      kind: str(raw.kind),
      status: str(raw.status),
      summary: strOrNull(raw.summary),
      summaryGeneric: bool(raw.summaryGeneric),
      toolName: strOrNull(raw.toolName),
      toolInput: strOrNull(raw.toolInput),
      resultBytes: numOrNull(raw.resultBytes),
      resultDigest: strOrNull(raw.resultDigest),
      model: strOrNull(raw.model),
      effort: strOrNull(raw.effort),
      thinkerId: strOrNull(raw.thinkerId),
      thinker: strOrNull(raw.thinker),
      tokens: {
        input: num(tokens.input),
        cacheWrite: num(tokens.cacheWrite),
        cacheRead: num(tokens.cacheRead),
        output: num(tokens.output),
        total: num(tokens.total),
      },
      // F-14: never trustworthy — kept on the wire shape, never rendered as currency.
      costMicros: numOrNull(raw.costMicros),
      actorUserId: strOrNull(raw.actorUserId),
      requestId: strOrNull(raw.requestId),
      sessionKey: strOrNull(raw.sessionKey),
      startedAt: strOrNull(raw.startedAt),
      endedAt: strOrNull(raw.endedAt),
      durationMs: numOrNull(raw.durationMs),
      error: strOrNull(raw.error),
    };
  };

  return {
    agents: {
      /**
       * `GET /api/thinkers` is the picker's authoritative set — what this workspace can
       * actually hand work to, local and cloud together. People can appear in it (a thinker is
       * whoever a conversation can address); an agents list is about the machines, so the
       * humans are left to the members surface.
       */
      list: async (): Promise<Agent[]> => {
        const raw = await transport.get("thinkers");
        return list(raw)
          .filter((entry) => entry.isHuman !== true)
          .map((entry) => ({
            id: str(entry.id),
            name: str(entry.name) || str(entry.id),
            description: str(entry.description),
            provider: str(entry.provider, "unknown"),
            category: strOrNull(entry.category),
            local: bool(entry.isLocal),
            usable: bool(entry.usable),
            avatarUrl: strOrNull(entry.avatarUrl),
          }));
      },
    },

    flows: {
      list: async (
        options: { includeArchived?: boolean; entity?: { kind: string; id: string } } = {},
      ): Promise<FlowList> => {
        const [raw, abilities] = await Promise.all([
          // The entity keys are sent ONLY when a filter was asked for, so an unfiltered call
          // puts exactly today's payload on the wire.
          transport.post("ai-flows.list", {
            includeArchived: options.includeArchived === true,
            ...(options.entity
              ? { entityKind: options.entity.kind, entityId: options.entity.id }
              : {}),
          }),
          manageAbilities([ABILITY.flowRun, ABILITY.flowUpdate, ABILITY.flowDelete]),
        ]);
        const payload = obj(raw);
        return {
          flows: list(payload.flows).map((entry) => toFlow(entry, abilities)),
          activeCount: num(payload.activeCount),
          activeLimit: num(payload.activePolicyLimit),
        };
      },

      classify: async (slug: string, purpose: "reusable" | "task-specific" | "unclassified") => {
        const raw = obj(await transport.post("ai-flows.classify", { slug, purpose }));
        return {
          categories: Array.isArray(raw.categories)
            ? raw.categories.filter((x): x is string => typeof x === "string")
            : [],
        };
      },

      get: async (slug: string): Promise<FlowDetail> => toFlowDetail(await flowRecord(slug), slug),

      /**
       * The record and its definition in one read. `ai-flows.get` returns `definitionJson` as a
       * STRING of JSON — the server holds the definition as a raw `JsonObject` and patches it in
       * place, which is exactly what makes unknown fields round-trip. Parsing is therefore the
       * adapter's job and the parsed value stays `unknown`: see the note on `FlowDetailSchema`.
       *
       * A definition that will not parse is reported as `null` rather than thrown. A studio that
       * cannot open a corrupt flow cannot be used to repair it, and the record itself — name,
       * status, version — is still perfectly readable.
       */
      definition: async (slug: string, version?: number): Promise<FlowDefinitionResult> => {
        const raw = await flowRecord(slug, version);
        return { flow: toFlowDetail(raw, slug), definition: parseDefinition(raw.definitionJson) };
      },

      /**
       * One Save, the whole definition.
       *
       * Two shape hazards, both paid for elsewhere already. First, the response comes in THREE
       * forms — the bare `AiFlowSaveResult`, and two envelopes that wrap it in `result` when the
       * definition carries a `permissions` block (`permissionsSynced` true or false). A client
       * that reads `res.version` works until the day somebody adds permissions, then silently
       * reports version 0. `unwrapSaveResult` collapses all three.
       *
       * Second, a REFUSED save is an HTTP 400 whose body carries the reasons. Throwing the
       * status message would lose them, so the transport's structured error is unpacked back
       * into a normal `{ok:false, errors:[…]}` result — the studio renders those in the same
       * band as its own client-side diagnostics, which is the only place an author can learn
       * about the rules only the server checks (§B.8).
       */
      save: async (input: {
        slug: string;
        definition: unknown;
        note?: string;
      }): Promise<FlowSaveResult> => {
        try {
          const raw = await transport.post("ai-flows.save", {
            slug: input.slug,
            definition: input.definition,
            ...(input.note?.trim() ? { note: input.note.trim() } : {}),
          });
          return unwrapSaveResult(raw);
        } catch (error) {
          const refusal = saveRefusal(error);
          if (refusal) {
            return refusal;
          }
          throw error;
        }
      },

      attachDoc: async (input: {
        slug: string;
        documentId?: string;
        collectionId?: string;
      }): Promise<FlowDocAttachment> => {
        const raw = obj(
          await transport.post("ai-flows.attach-doc", {
            slug: input.slug,
            // Both keys are omitted rather than sent empty: an absent `documentId` is what
            // ASKS the server to materialize a living document, and `""` would not.
            ...(input.documentId ? { documentId: input.documentId } : {}),
            ...(input.collectionId ? { collectionId: input.collectionId } : {}),
          }),
        );
        return {
          documentId: str(raw.documentId),
          created: bool(raw.created),
          title: strOrNull(raw.title),
          urlId: strOrNull(raw.urlId),
        };
      },

      nodeHistory: async (input: {
        slug: string;
        nodeId: string;
        limit?: number;
      }): Promise<FlowNodeHistory> => {
        const raw = obj(
          await transport.post("ai-flows.node-history", {
            slug: input.slug,
            nodeId: input.nodeId,
            // The server clamps to 1..20; asking for more is silently ignored, so clamp here
            // too rather than pretend a larger page was requested.
            limit: Math.min(20, Math.max(1, input.limit ?? 3)),
          }),
        );
        return {
          nodeId: str(raw.nodeId, input.nodeId),
          slug: strOrNull(raw.slug),
          matchedBy: str(raw.matchedBy, "none"),
          matchNote: str(raw.matchNote),
          runs: list(raw.runs).map((entry) => {
            const tokens = obj(entry.tokens);
            return {
              runId: str(entry.runId),
              runName: str(entry.runName),
              runStatus: str(entry.runStatus),
              stepStatus: str(entry.stepStatus),
              startedAt: strOrNull(entry.startedAt),
              endedAt: strOrNull(entry.endedAt),
              durationMs: numOrNull(entry.durationMs),
              error: strOrNull(entry.error),
              actions: num(entry.actions),
              tokens: {
                input: num(tokens.input),
                cacheWrite: num(tokens.cacheWrite),
                cacheRead: num(tokens.cacheRead),
                output: num(tokens.output),
                total: num(tokens.total),
              },
              contentTokens: num(entry.contentTokens),
              amplification: numOrNull(entry.amplification),
              cacheReadShare: numOrNull(entry.cacheReadShare),
            };
          }),
        };
      },

      versions: async (slug: string): Promise<FlowVersion[]> => {
        const raw = obj(await transport.post("ai-flows.versions", { slug }));
        return list(raw.data).map((entry) => ({
          version: num(entry.version),
          createdAt: str(entry.createdAt, new Date(0).toISOString()),
          createdBy: str(entry.createdBy),
          note: strOrNull(entry.note),
          isHead: bool(entry.isHead),
        }));
      },

      setStatus: async (slug: string, status: FlowStatus): Promise<Flow> => {
        await transport.post("ai-flows.status", { slug, status: flowStatusWire(status) });
        // The status endpoint answers with an acknowledgement, not a flow; re-read the row so
        // the caller gets the same shape every other flow call returns.
        const [raw, abilities] = await Promise.all([
          transport.post("ai-flows.get", { slug }),
          manageAbilities([ABILITY.flowRun, ABILITY.flowUpdate, ABILITY.flowDelete]),
        ]);
        return toFlow({ ...obj(raw), updatedAt: new Date().toISOString() }, abilities);
      },

      /**
       * There is no synchronous answer to "run this". `auto` lets the server host the flow
       * when its definition allows; otherwise the work is queued for an editor window to claim,
       * and if none does it expires as the record of the attempt. The note says which happened,
       * verbatim from the server — this is the one place where guessing would mislead.
       */
      run: async (slug: string, request: FlowRunRequest = {}): Promise<FlowRunAck> => {
        const raw = obj(
          await transport.post("ai-flows.run", {
            slug,
            host: request.host ?? "auto",
            ...(request.prompt?.trim() ? { prompt: request.prompt.trim() } : {}),
            // Aim at one window, or spend at most this much — both omitted rather than sent
            // empty, because `targetSessionKey: ""` is a request for a window called "".
            ...(request.targetSessionKey ? { targetSessionKey: request.targetSessionKey } : {}),
            ...(typeof request.tokenBudget === "number" && request.tokenBudget > 0
              ? { tokenBudget: request.tokenBudget }
              : {}),
            ...(request.itemKey ? { itemKey: request.itemKey } : {}),
          }),
        );
        return {
          host: str(raw.host, "ide"),
          runId: strOrNull(raw.runId),
          dispatchId: strOrNull(raw.dispatchId),
          target: strOrNull(raw.target),
          note: str(raw.note),
        };
      },

      delete: async (slug: string): Promise<void> => {
        await transport.post("ai-flows.delete", { slug });
      },
    },

    /**
     * The machinery board: one `tasks.list` snapshot covering scheduled work, live runs and
     * dispatches, each row carrying its own health rollup. Board rows carry no policy (D.2
     * rule 6), so writes against them are gated on the workspace policy, same as flows.
     */
    board: {
      snapshot: async (
        query: { activeOnly?: boolean; limit?: number } = {},
      ): Promise<TaskBoard> => {
        const raw = obj(
          await transport.post("tasks.list", {
            ...(query.activeOnly ? { activeOnly: true } : {}),
            ...(query.limit ? { limit: query.limit } : {}),
          }),
        );
        const infra = obj(raw.infrastructure);
        return {
          tasks: list(raw.tasks).map(toTaskRow),
          worstHealth: healthLevel(raw.worstHealth),
          warnings: Array.isArray(raw.warnings) ? raw.warnings.map((entry) => str(entry)) : [],
          infrastructure: {
            workerProcessingJobs: infra.workerProcessingJobs !== false,
            liveIdeSessions: num(infra.liveIdeSessions),
          },
          checkedAt: str(raw.checkedAt, new Date().toISOString()),
        };
      },
    },

    /** The command ledger: every AI action the workspace ran, grouped, filtered or per-run. */
    ledger: {
      actions: async (query: LedgerQuery = {}): Promise<LedgerPage> => {
        const raw = obj(
          await transport.post("ai-actions.list", {
            limit: query.limit ?? 200,
            offset: query.offset ?? 0,
            ...(query.runId ? { runId: query.runId } : {}),
            ...(query.actorUserId ? { actorUserId: query.actorUserId } : {}),
            ...(query.stepId ? { stepId: query.stepId } : {}),
            ...(query.toolName ? { toolName: query.toolName } : {}),
            ...(query.kind ? { kind: query.kind } : {}),
            ...(query.failedOnly ? { failedOnly: true } : {}),
          }),
        );
        return {
          total: num(raw.total),
          limit: num(raw.limit, 200),
          offset: num(raw.offset),
          actions: list(raw.actions).map(toActionRow),
        };
      },

      report: async (query: ReportQuery = {}): Promise<LedgerReport> => {
        const raw = obj(
          await transport.post("ai-actions.report", {
            ...(query.runId ? { runId: query.runId } : {}),
            ...(query.from ? { from: query.from } : {}),
            ...(query.to ? { to: query.to } : {}),
            groupBy: query.groupBy ?? "tool",
          }),
        );
        const totals = obj(raw.totals);
        return {
          groupBy: str(raw.groupBy, query.groupBy ?? "tool"),
          rows: list(raw.rows).map((row) => ({
            key: str(row.key),
            calls: num(row.calls),
            inputTokens: num(row.inputTokens),
            cacheWriteTokens: num(row.cacheWriteTokens),
            cacheReadTokens: num(row.cacheReadTokens),
            outputTokens: num(row.outputTokens),
            totalTokens: num(row.totalTokens),
            resultBytes: num(row.resultBytes),
            costMicros: numOrNull(row.costMicros),
          })),
          totals: {
            input: num(totals.input),
            cacheWrite: num(totals.cacheWrite),
            cacheRead: num(totals.cacheRead),
            output: num(totals.output),
            total: num(totals.total),
            contentTokens: num(totals.contentTokens),
            amplification: numOrNull(totals.amplification),
            cacheReadShare: numOrNull(totals.cacheReadShare),
            actions: num(totals.actions),
            requests: num(totals.requests),
            costMicros: numOrNull(totals.costMicros),
          },
        };
      },

      efficiency: async (runId: string): Promise<RunEfficiency> => {
        const raw = obj(await transport.post("ai-actions.efficiency", { runId }));
        const run = obj(raw.run);
        const nodes = list(raw.nodes).map((node) => ({
          nodeId: str(node.nodeId),
          index: num(node.index),
          requests: num(node.requests),
          actions: num(node.actions),
          filesWritten: num(node.filesWritten),
          contentTokens: num(node.contentTokens),
          billedTokens: num(node.billedTokens),
          cacheReadTokens: num(node.cacheReadTokens),
          cacheReadShare: numOrNull(node.cacheReadShare),
          amplification: numOrNull(node.amplification),
          models: Array.isArray(node.models)
            ? node.models.map((entry) => (typeof entry === "string" ? entry : null))
            : [],
          effortMax: strOrNull(node.effortMax),
        }));
        // Not sent by the server (F-11): derived the same way kms derives it, and labelled
        // as derived wherever it is rendered.
        const billed = num(run.billedTokens);
        const cacheRead = nodes.reduce((sum, node) => sum + node.cacheReadTokens, 0);
        return {
          runId: str(raw.runId, runId),
          nodes,
          run: {
            billedTokens: billed,
            contentTokens: num(run.contentTokens),
            amplification: numOrNull(run.amplification),
            requests: num(run.requests),
            actions: num(run.actions),
            tokenBudget: numOrNull(run.tokenBudget),
            budgetAction: str(run.budgetAction, "pause"),
            budgetTrippedAt: strOrNull(run.budgetTrippedAt),
          },
          worstNode: strOrNull(raw.worstNode),
          runCacheReadShare: billed > 0 ? cacheRead / billed : null,
        };
      },

      setBudget: async (
        runId: string,
        tokenBudget: number | null,
        action: "pause" | "warn" = "pause",
      ): Promise<BudgetAck> => {
        const raw = obj(await transport.post("ai-actions.budget", { runId, tokenBudget, action }));
        return {
          ok: bool(raw.ok),
          runId: str(raw.runId, runId),
          tokenBudget: numOrNull(raw.tokenBudget),
          budgetAction: str(raw.budgetAction, action),
          spent: num(raw.spent),
        };
      },
    },

    runs: {
      list: async (query: RunQuery = {}): Promise<Run[]> => {
        const raw = obj(
          await transport.post("runs.list", {
            limit: query.limit ?? 50,
            ...(query.status ? { status: query.status } : {}),
            ...(query.kind ? { kind: query.kind } : {}),
            ...(query.activeOnly ? { activeOnly: true } : {}),
          }),
        );
        return list(raw.runs).map(toRun);
      },

      get: async (id: string): Promise<Run> => {
        const raw = obj(await transport.post("runs.get", { runId: id }));
        return toRun(obj(raw.run));
      },

      /**
       * The viewer's shape (D.2 rule 4) — the thin `Run` plus graph/children/host/statusReason,
       * none of which the list needs. A separate method rather than widening `get` keeps a run
       * list from paying for a graph snapshot.
       */
      detail: async (id: string): Promise<RunDetail> => {
        const raw = obj(await transport.post("runs.get", { runId: id }));
        return {
          run: toRun(obj(raw.run)),
          host: raw.host === "server" || raw.host === "ide" ? raw.host : null,
          statusReason: strOrNull(raw.statusReason),
          graph: raw.graph && typeof raw.graph === "object" ? (raw.graph as Json) : null,
          causalRunId: strOrNull(raw.causalRunId),
          children: list(raw.children).map(toChildRun),
        };
      },

      /**
       * Weave (15 §7.2): the node ⇄ tile read. Shaped defensively — a server that predates Weave
       * answers 404/none, and the canvas must still draw, so every field has an honest default.
       */
      tree: async (id: string): Promise<RunTree> => {
        const raw = obj(await transport.post("runs.tree", { runId: id }));
        const root = obj(raw.root);
        return {
          runId: str(raw.runId, id),
          flowSlug: strOrNull(raw.flowSlug),
          status: str(raw.status, "unknown"),
          statusReason: strOrNull(raw.statusReason),
          isProject: bool(raw.isProject),
          reshapeSeq: num(raw.reshapeSeq),
          root: root.key
            ? {
                id: str(root.id),
                key: str(root.key),
                kind: str(root.kind, "epic"),
                title: str(root.title),
                state: str(root.state, "new"),
                category: str(root.category, "proposed"),
              }
            : null,
          nodes: list(raw.nodes).map((node) => {
            const tile = obj(node.tile);
            const assignee = obj(tile.assignee);
            return {
              nodeId: str(node.nodeId),
              label: str(node.label) || str(node.nodeId),
              kind: str(node.kind, "task"),
              executor: str(node.executor, "thinker"),
              ownerRole: strOrNull(node.ownerRole),
              model: strOrNull(node.model),
              requireApproval: bool(node.requireApproval),
              tile: tile.key
                ? {
                    id: str(tile.id),
                    key: str(tile.key),
                    kind: str(tile.kind, "task"),
                    state: str(tile.state, "new"),
                    category: str(tile.category, "proposed"),
                    title: str(tile.title),
                    priority: strOrNull(tile.priority),
                    severity: strOrNull(tile.severity),
                    estimate: numOrNull(tile.estimate),
                    blocked: bool(tile.blocked),
                    blockedReason: strOrNull(tile.blockedReason),
                    held: bool(tile.held),
                    dueAt: strOrNull(tile.dueAt),
                    assignee: assignee.kind
                      ? { kind: str(assignee.kind), id: strOrNull(assignee.id) }
                      : null,
                    role: str(tile.role, "node"),
                    parentNodeId: strOrNull(tile.parentNodeId),
                    isRef: bool(tile.isRef),
                    version: num(tile.version),
                  }
                : null,
              runStatus: str(node.runStatus, "pending"),
              queuedReason: strOrNull(node.queuedReason),
              branchId: strOrNull(node.branchId),
              attempts: num(node.attempts),
              questionKey: strOrNull(node.questionKey),
            };
          }),
          edges: list(raw.edges).map((edge) => ({
            from: str(edge.from),
            to: str(edge.to),
            condition: str(edge.condition, "always"),
          })),
        };
      },

      steps: async (id: string): Promise<RunStep[]> => {
        const raw = obj(await transport.post("runs.steps", { runId: id }));
        return list(raw.steps).map((step) => {
          const meta = obj(step.meta);
          const output = preview(step.output, bool(step.outputTruncated));
          return {
            index: num(step.index),
            nodeId: str(step.nodeId),
            name: str(step.name) || str(step.nodeId),
            status: str(step.status, "pending"),
            startedAt: strOrNull(step.startedAt),
            endedAt: strOrNull(step.endedAt),
            durationMs: numOrNull(step.durationMs),
            output: output.text,
            outputTruncated: output.truncated,
            error: strOrNull(step.error),
            model: strOrNull(meta.model),
            tokens: numOrNull(meta.totalTokens),
            // Kept whole (not just model/totalTokens) for the run viewer's node card — the
            // "Prompt this node received" collapsible reads meta.prompt/meta.promptChars.
            meta: Object.keys(meta).length > 0 ? meta : null,
            log: list(step.log).map((line) => ({ at: str(line.at), message: str(line.message) })),
          };
        });
      },

      logs: async (id: string): Promise<RunLogLine[]> => {
        const raw = obj(await transport.post("runs.logs", { runId: id }));
        return list(raw.lines).map((line, index) => ({
          id: String(index),
          at: str(line.at, new Date(0).toISOString()),
          nodeId: strOrNull(line.nodeId),
          level: str(line.level, "info"),
          message: str(line.message),
        }));
      },

      stats: async (query: { since?: string; kind?: string } = {}): Promise<RunStats> => {
        const raw = obj(
          await transport.post("runs.stats", {
            ...(query.since ? { since: query.since } : {}),
            ...(query.kind ? { kind: query.kind } : {}),
          }),
        );
        const totals = obj(raw.totals);
        const duration = obj(raw.duration);
        const tokens = obj(raw.tokens);
        const byStatus = obj(totals.byStatus);
        const byKind = obj(totals.byKind);

        return {
          windowDays: num(raw.windowDays, 14),
          totalRuns: num(totals.runs),
          byStatus: Object.fromEntries(
            Object.entries(byStatus).map(([key, value]) => [key, num(value)]),
          ),
          byKind: Object.fromEntries(
            Object.entries(byKind).map(([key, value]) => [key, num(value)]),
          ),
          byName: list(totals.byName).map((entry) => ({
            name: str(entry.name),
            count: num(entry.count),
            successRate: num(entry.successRate),
            avgDurationMs: numOrNull(entry.avgDurationMs),
            tokens: num(entry.tokens),
          })),
          successRate: num(raw.successRate),
          terminalRuns: num(raw.terminalRuns),
          avgDurationMs: numOrNull(duration.avgMs),
          maxDurationMs: numOrNull(duration.maxMs),
          sampledRuns: num(duration.sampledRuns),
          tokens: {
            input: num(tokens.input),
            output: num(tokens.output),
            total: num(tokens.total),
          },
          daily: list(raw.daily).map((day) => ({
            date: str(day.date),
            total: num(day.total),
            completed: num(day.completed),
            failed: num(day.failed),
          })),
        };
      },

      control: async (id: string, action: RunControl): Promise<void> => {
        await transport.post(`runs.${action}`, { runId: id });
      },

      /**
       * The identity on the record is the authenticated caller, decided server-side, and the
       * decision is labelled automation for every channel that is not a browser. Slate sends no
       * `X-Docit-Channel` header, so an answer given here is recorded as what it is: a human's.
       */
      approve: async (id: string, decision: RunApproval): Promise<void> => {
        await transport.post("runs.approve", {
          runId: id,
          nodeId: decision.nodeId,
          approved: decision.approved,
          // F-2: the server's ApproveRequest field is `directive`, not `note` — sending `note`
          // silently dropped it, so "deny with a correction" never worked from Slate.
          ...(decision.directive?.trim() ? { directive: decision.directive.trim() } : {}),
        });
      },

      /**
       * Two verbs, never one ambiguous button (§A.4). `graceful` lets the current node finish;
       * `immediate` makes server state true right now. Conflating them is how a run kept
       * spending ~1.4M tokens/hour after a "stop" that only ever meant "stop eventually".
       */
      stop: async (
        id: string,
        mode: "graceful" | "immediate",
        reason?: string,
      ): Promise<StopAck> => {
        const raw = obj(
          await transport.post("runs.stop", {
            runId: id,
            mode,
            ...(reason?.trim() ? { reason: reason.trim() } : {}),
          }),
        );
        const executor = raw.executor ? obj(raw.executor) : null;
        return {
          ok: bool(raw.ok),
          mode: raw.mode === "immediate" ? "immediate" : "graceful",
          honoredAt: str(raw.honoredAt),
          executor: executor
            ? { ideSessionKey: strOrNull(executor.ideSessionKey), pid: numOrNull(executor.pid) }
            : null,
          executorNote: str(raw.executorNote),
        };
      },

      step: async (id: string, options: { stop?: boolean } = {}): Promise<StepAck> => {
        const raw = obj(
          await transport.post("runs.step", {
            runId: id,
            ...(options.stop ? { stop: true } : {}),
          }),
        );
        return {
          ok: bool(raw.ok),
          stepMode: bool(raw.stepMode),
          released: raw.released === undefined ? null : bool(raw.released),
          honoredAt: str(raw.honoredAt),
          executor: raw.executor ?? null,
          host: strOrNull(raw.host),
        };
      },

      jump: async (id: string, target: JumpRequest): Promise<JumpAck> => {
        const raw = obj(
          await transport.post("runs.jump", {
            runId: id,
            toNodeId: target.toNodeId,
            mode: target.mode ?? "rewind",
            ...(target.note?.trim() ? { note: target.note.trim() } : {}),
          }),
        );
        return {
          ok: bool(raw.ok),
          mode: str(raw.mode, target.mode ?? "rewind"),
          toNodeId: str(raw.toNodeId, target.toNodeId),
          invalidated: list(raw.invalidated).map((entry) => ({
            nodeId: str(entry.nodeId),
            index: num(entry.index),
            was: str(entry.was),
          })),
          warning: strOrNull(raw.warning),
          executor: raw.executor ?? null,
          executorNote: str(raw.executorNote),
        };
      },

      /** ALWAYS the full list — an empty array clears every breakpoint. */
      breakpoints: async (id: string, nodeIds: string[]): Promise<string[]> => {
        const raw = obj(await transport.post("runs.breakpoints", { runId: id, nodeIds }));
        return Array.isArray(raw.breakpoints)
          ? raw.breakpoints.map((entry) => str(entry))
          : [...nodeIds];
      },

      /** Only `pulse-*` kinds can retry (F-5); `can.retry` already says so before this is called. */
      retry: async (id: string): Promise<{ runId: string }> => {
        const raw = obj(await transport.post("runs.retry", { runId: id }));
        return { runId: str(raw.runId, id) };
      },

      publish: async (id: string, options: PublishOptions = {}): Promise<PublishResult> => {
        const raw = obj(
          await transport.post("runs.publish", {
            runId: id,
            ...(options.artifactPath ? { artifactPath: options.artifactPath } : {}),
            ...(options.destination ? { destination: options.destination } : {}),
          }),
        );
        return {
          ok: bool(raw.ok),
          documentId: str(raw.documentId),
          title: str(raw.title),
          url: str(raw.url),
          mode: str(raw.mode),
        };
      },

      /** REST GETs with no query-param helper on the transport — the caller builds the string. */
      artifacts: async (id: string): Promise<RunArtifact[]> => {
        const raw = obj(await transport.get(`ide-runs.artifacts?runId=${encodeURIComponent(id)}`));
        return list(raw.artifacts).map((entry) => ({
          path: str(entry.path),
          version: num(entry.version),
          nodeId: strOrNull(entry.nodeId),
          mediaType: strOrNull(entry.mediaType),
          bytes: num(entry.bytes),
          sha256: strOrNull(entry.sha256),
          producedBy: strOrNull(entry.producedBy),
          createdAt: str(entry.createdAt, new Date(0).toISOString()),
          hasContent: bool(entry.hasContent),
          sensitive: false,
        }));
      },

      artifactContent: async (
        id: string,
        path: string,
        version?: number,
      ): Promise<ArtifactContent> => {
        const query = `runId=${encodeURIComponent(id)}&path=${encodeURIComponent(path)}${
          version !== undefined ? `&version=${version}` : ""
        }`;
        const raw = obj(await transport.get(`ide-runs.artifact-content?${query}`));
        return {
          path: str(raw.path, path),
          version: num(raw.version, version ?? 1),
          nodeId: strOrNull(raw.nodeId),
          mediaType: strOrNull(raw.mediaType),
          bytes: num(raw.bytes),
          sha256: strOrNull(raw.sha256),
          producedBy: strOrNull(raw.producedBy),
          createdAt: str(raw.createdAt, new Date(0).toISOString()),
          content: strOrNull(raw.content),
          contentWithheld: bool(raw.contentWithheld),
        };
      },
    },

    /**
     * Directives: forward-only steering messages a live flow's nodes can read. No per-entity
     * policy on the wire, so writes ride the workspace policy like flow/schedule control.
     */
    directives: {
      list: async (flowId: string, activeOnly = true): Promise<Directive[]> => {
        const raw = obj(await transport.post("directives.list", { flowId, activeOnly }));
        return list(raw.data).map((entry) => ({
          id: str(entry.id),
          flowId: str(entry.flowId, flowId),
          text: str(entry.text),
          createdBy: strOrNull(entry.createdBy),
          active: bool(entry.active),
          createdAt: str(entry.createdAt, new Date(0).toISOString()),
          revokedAt: strOrNull(entry.revokedAt),
          revokedBy: strOrNull(entry.revokedBy),
        }));
      },

      create: async (flowId: string, text: string): Promise<Directive> => {
        const raw = obj(await transport.post("directives.create", { flowId, text }));
        const data = obj(raw.data);
        return {
          id: str(data.id),
          flowId: str(data.flowId, flowId),
          text: str(data.text, text),
          createdBy: strOrNull(data.createdBy),
          active: data.active === undefined ? true : bool(data.active),
          createdAt: str(data.createdAt, new Date().toISOString()),
          revokedAt: strOrNull(data.revokedAt),
          revokedBy: strOrNull(data.revokedBy),
        };
      },

      revoke: async (id: string): Promise<void> => {
        await transport.post("directives.revoke", { id });
      },
    },

    tasks: {
      list: async (): Promise<ScheduledTask[]> => {
        const [raw, abilities] = await Promise.all([
          transport.post("scheduled-flows.list", {}),
          manageAbilities([ABILITY.taskManage]),
        ]);
        return list(obj(raw).tasks).map((entry) => toTask(entry, abilities));
      },

      /**
       * Slate schedules flows. The platform also carries digest and advisor tasks, which have
       * their own authoring surfaces there; this creates the kind Slate can honestly describe,
       * and lists the rest read-only rather than offering to edit what it cannot render.
       */
      create: async (draft: ScheduledTaskDraft): Promise<void> => {
        await transport.post("scheduled-flows.create", {
          title: draft.title,
          ideFlow: {
            slug: draft.flowSlug,
            ...(draft.prompt?.trim() ? { prompt: draft.prompt.trim() } : {}),
            ...(draft.targetSessionKey ? { targetSessionKey: draft.targetSessionKey } : {}),
            ...(draft.tokenBudget ? { tokenBudget: draft.tokenBudget } : {}),
          },
          trigger: serverTrigger(draft.trigger),
        });
      },

      /** Task Center's digest-authoring mode: a topic/source set that writes into a document. */
      createDigest: async (draft: DigestTaskDraft): Promise<void> => {
        await transport.post("scheduled-flows.create", {
          ...(draft.title?.trim() ? { title: draft.title.trim() } : {}),
          digest: {
            ...(draft.topic?.trim() ? { topic: draft.topic.trim() } : {}),
            sourceMode: draft.sourceMode,
            sources: draft.sources ?? [],
            ...(draft.thinkerId ? { thinkerId: draft.thinkerId } : {}),
            targetDocumentId: draft.targetDocumentId,
            outputMode: draft.outputMode,
            notifyInApp: draft.notifyInApp === true,
          },
          trigger: serverTrigger(draft.trigger),
        });
      },

      /** Task Center's plain-flow mode — no IDE window needed for the flow to fire. */
      createFlow: async (draft: FlowTaskDraft): Promise<void> => {
        await transport.post("scheduled-flows.create", {
          flowId: draft.flowId,
          title: draft.title,
          trigger: serverTrigger(draft.trigger),
        });
      },

      setPaused: async (id: string, paused: boolean): Promise<void> => {
        await transport.post(paused ? "scheduled-flows.pause" : "scheduled-flows.resume", {
          decisionId: id,
        });
      },

      runNow: async (id: string): Promise<{ note: string | null }> => {
        const raw = obj(await transport.post("scheduled-flows.run-now", { decisionId: id }));
        return { note: strOrNull(raw.note) };
      },

      delete: async (id: string): Promise<void> => {
        await transport.post("scheduled-flows.delete", { decisionId: id });
      },

      /**
       * The prompt composer routes through here and returns a PROPOSAL — it prefills the create
       * form and creates nothing (propose → confirm → run, never propose → create directly).
       *
       * ONE endpoint, SEVEN shapes (W7 §B1.1). The parse is `normalizeRouteProposal`, shared
       * verbatim with the REST transport's schema so the two can never disagree about which
       * union member a body is.
       *
       * `routeId` is sent **only where the surface can use it**: the server reads it on the
       * `document` and `collection` branches and ignores it everywhere else, and kms sent the
       * last-visited document id on every screen (§E6.2). Sending it where it is inert is not
       * harmless — it is a lie about what the request is about.
       */
      route: async (
        prompt: string,
        surface: RouteSurface = "tasks",
        routeId?: string,
      ): Promise<RouteProposal> => {
        const carriesRouteId = surface === "document" || surface === "collection";
        return normalizeRouteProposal(
          await transport.post("ai-actions.route", {
            prompt,
            surface,
            ...(carriesRouteId && routeId ? { routeId } : {}),
          }),
        );
      },
    },

    dispatches: {
      list: async (options: { activeOnly?: boolean; limit?: number } = {}): Promise<Dispatch[]> => {
        const raw = obj(
          await transport.post("ai-dispatch.list", {
            limit: options.limit ?? 25,
            ...(options.activeOnly ? { activeOnly: true } : {}),
          }),
        );
        return list(raw.dispatches).map((entry) => ({
          id: str(entry.id),
          kind: str(entry.kind),
          agent: str(entry.agent),
          status: str(entry.status).toLowerCase(),
          target: strOrNull(entry.targetSessionKey),
          claimedBy: strOrNull(entry.claimedBySessionKey),
          runId: strOrNull(entry.runId),
          createdAt: str(entry.createdAt, new Date(0).toISOString()),
          claimedAt: strOrNull(entry.claimedAt),
          expiresAt: strOrNull(entry.expiresAt),
          completedAt: strOrNull(entry.completedAt),
          result: strOrNull(entry.result),
          error: strOrNull(entry.error),
          redacted: bool(entry.redacted),
          // W4: the nine fields the server already sent and this mapping dropped. The
          // approval card, the 2 s result poll and the person-facing status word all read
          // them, and a field that arrives as `undefined` is indistinguishable from one the
          // server does not send — so every one is mapped explicitly.
          originSessionKey: strOrNull(entry.originSessionKey),
          conversationId: strOrNull(entry.conversationId),
          tokenBudget: numOrNull(entry.tokenBudget),
          payload: strOrNull(entry.payload),
          isOwn: entry.isOwn !== false,
          statusDetail: strOrNull(entry.statusDetail),
          queuedBehind: num(entry.queuedBehind),
          ownerUserName: strOrNull(entry.ownerUserName),
          run: entry.run
            ? {
                status: str(obj(entry.run).status),
                currentNodeId: strOrNull(obj(entry.run).currentNodeId),
                awaitingApprovalNodeId: strOrNull(obj(entry.run).awaitingApprovalNodeId),
              }
            : null,
        }));
      },

      cancel: async (id: string, reason?: string): Promise<void> => {
        await transport.post("ai-dispatch.cancel", {
          id,
          ...(reason?.trim() ? { reason: reason.trim() } : {}),
        });
      },
    },

    /** Connected editor windows — Task Center's target-window filter. */
    ideSessions: {
      list: async (): Promise<IdeSession[]> => {
        const raw = obj(await transport.post("ide-sessions.list", {}));
        return list(raw.data).map((entry) => ({
          id: str(entry.id),
          sessionKey: str(entry.sessionKey),
          userId: strOrNull(entry.userId),
          userName: strOrNull(entry.userName),
          isOwn: bool(entry.isOwn),
          machineName: strOrNull(entry.machineName),
          workspaceName: strOrNull(entry.workspaceName),
          workspacePath: strOrNull(entry.workspacePath),
          extensionVersion: strOrNull(entry.extensionVersion),
          bridgeVersion: strOrNull(entry.bridgeVersion),
          machineId: strOrNull(entry.machineId),
          boundTeamId: strOrNull(entry.boundTeamId),
          bindingMismatch: entry.bindingMismatch === undefined ? null : bool(entry.bindingMismatch),
          capabilities: Array.isArray(entry.capabilities)
            ? entry.capabilities.map((cap) => str(cap))
            : [],
          startedAt: str(entry.startedAt, new Date(0).toISOString()),
          lastSeenAt: str(entry.lastSeenAt, new Date(0).toISOString()),
          revokedAt: strOrNull(entry.revokedAt),
          isOnline: bool(entry.isOnline),
        }));
      },
    },

    activity: {
      /** The one automation endpoint that speaks the document surface's envelope. */
      list: async (query: ActivityQuery = {}): Promise<ActivityPage> => {
        const raw = obj(
          await transport.post("events.list", {
            offset: query.offset ?? 0,
            limit: query.limit ?? 25,
            ...(query.auditLog ? { auditLog: true } : {}),
          }),
        );
        const pagination = obj(raw.pagination);
        return {
          events: list(raw.data).map((entry) => {
            const actor = entry.actor ? obj(entry.actor) : null;
            return {
              id: str(entry.id),
              name: str(entry.name),
              actor: actor
                ? {
                    id: str(actor.id),
                    name: str(actor.name),
                    avatarUrl: strOrNull(actor.avatarUrl),
                  }
                : null,
              createdAt: str(entry.createdAt, new Date(0).toISOString()),
              documentId: strOrNull(entry.documentId),
              collectionId: strOrNull(entry.collectionId),
            };
          }),
          offset: num(pagination.offset),
          limit: num(pagination.limit, 25),
          total: num(pagination.total),
        };
      },
    },
  };
}
